import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    ColorSwatchPicker,
    ColorSwatch
} from "ncore-ui-kit";
import {
    BulletList,
    DocsTable,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "ColorSwatchPicker";

const USAGE_CODE = `const [color, setColor] = useState("#3b6fb6");

<ColorSwatchPicker
    items={[
        {
            title: "Teal",
            color: "#2f8f83"
        },
        {
            title: "Brick",
            color: "#b4532a"
        },
        {
            title: "Blue",
            color: "#3b6fb6"
        }
    ]}
    onChange={setColor}
    value={color}
/>`;

const ADD_CODE = `<ColorSwatchPicker
    onAddPress={openColorPicker}
    isAddEnabled={true}
    items={items}
/>`;

const GRID_CODE = `<ColorSwatchPicker
    layout="grid"
    items={items}
    columns={4}
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const START_COLOR = "#3b6fb6";

const GRID_COLUMNS = 4;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorSwatchPickerPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("colorSwatchPickerPage.addTitle"),
        id: "add"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ColorSwatchPickerPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        color,
        setColor
    ] = useState<string>(START_COLOR);

    const [
        addCount,
        setAddCount
    ] = useState<number>(0);

    const items = [
        {
            title: t("colorSwatchPickerPage.teal"),
            color: "#2f8f83"
        },
        {
            title: t("colorSwatchPickerPage.brick"),
            color: "#b4532a"
        },
        {
            title: t("colorSwatchPickerPage.blue"),
            color: "#3b6fb6"
        },
        {
            title: t("colorSwatchPickerPage.mustard"),
            color: "#c79a1e"
        },
        {
            title: t("colorSwatchPickerPage.purple"),
            color: "#7b5aa6"
        },
        {
            title: t("colorSwatchPickerPage.green"),
            color: "#5a8f3c"
        },
        {
            title: t("colorSwatchPickerPage.rose"),
            color: "#b8456b"
        },
        {
            title: t("colorSwatchPickerPage.grey"),
            color: "#6b6b6b"
        }
    ];

    return <Article>
        <ComponentHeader
            lead={t("colorSwatchPickerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorSwatchPickerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("colorSwatchPickerPage.overviewCaption", [
                    color
                ])}
                isStacked={true}
            >
                <ColorSwatchPicker
                    onChange={setColor}
                    items={items}
                    value={color}
                />
                <ColorSwatch
                    color={color}
                    size="large"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("colorSwatchPickerPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("colorSwatchPickerPage.layoutText")}
            </Paragraph>
            <Preview>
                <ColorSwatchPicker
                    initialValue={START_COLOR}
                    columns={GRID_COLUMNS}
                    items={items}
                    layout="grid"
                />
            </Preview>
            <CodeBlock
                code={GRID_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("colorSwatchPickerPage.addTitle")}
            id="add"
        >
            <Paragraph>
                {t("colorSwatchPickerPage.addText")}
            </Paragraph>
            <Preview
                caption={t("colorSwatchPickerPage.addCaption", [
                    addCount
                ])}
            >
                <ColorSwatchPicker
                    onAddPress={() => setAddCount(addCount + 1)}
                    items={items.slice(0, GRID_COLUMNS)}
                    initialValue={START_COLOR}
                    isAddEnabled={true}
                />
            </Preview>
            <CodeBlock
                code={ADD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("colorSwatchPickerPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <ColorSwatchPicker
                        items={items.slice(0, GRID_COLUMNS)}
                        initialValue={START_COLOR}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("colorSwatchPickerPage.statesText")}
            </Paragraph>
            <Preview>
                <ColorSwatchPicker
                    items={items.slice(0, GRID_COLUMNS)}
                    initialValue={START_COLOR}
                    isDisabled={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("colorSwatchPickerPage.a11yRole"),
                    t("colorSwatchPickerPage.a11yName"),
                    t("colorSwatchPickerPage.a11yTabStop"),
                    t("colorSwatchPickerPage.a11yDisabled")
                ]}
            />
            <DocsTable
                rows={[
                    [
                        t("colorSwatchPickerPage.keyArrows"),
                        t("colorSwatchPickerPage.actionArrows")
                    ],
                    [
                        t("colorSwatchPickerPage.keyUpDown"),
                        t("colorSwatchPickerPage.actionUpDown")
                    ],
                    [
                        t("colorSwatchPickerPage.keyHomeEnd"),
                        t("colorSwatchPickerPage.actionHomeEnd")
                    ],
                    [
                        t("colorSwatchPickerPage.keySelect"),
                        t("colorSwatchPickerPage.actionSelect")
                    ]
                ]}
                headers={[
                    t("colorSwatchPickerPage.keyHeader"),
                    t("colorSwatchPickerPage.actionHeader")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("colorSwatchPickerPage.propItems"),
                        type: "Array<{ title: string; color: string }>",
                        name: "items"
                    },
                    {
                        description: t("colorSwatchPickerPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("colorSwatchPickerPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        type: "(value: string, item: ColorSwatchPickerItem) => void",
                        description: t("colorSwatchPickerPage.propOnChange"),
                        name: "onChange"
                    },
                    {
                        description: t("colorSwatchPickerPage.propLayout"),
                        type: "\"row\" | \"grid\"",
                        defaultValue: "\"row\"",
                        name: "layout"
                    },
                    {
                        description: t("colorSwatchPickerPage.propColumns"),
                        defaultValue: "8",
                        name: "columns",
                        type: "number"
                    },
                    {
                        description: t("colorSwatchPickerPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("colorSwatchPickerPage.propIsAddEnabled"),
                        defaultValue: "false",
                        name: "isAddEnabled",
                        type: "boolean"
                    },
                    {
                        description: t("colorSwatchPickerPage.propOnAddPress"),
                        type: "() => void",
                        name: "onAddPress"
                    },
                    {
                        description: t("colorSwatchPickerPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("colorSwatchPickerPage.propSurfaceColor"),
                        name: "surfaceColor",
                        type: "string"
                    },
                    {
                        description: t("colorSwatchPickerPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("colorSwatchPickerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("colorSwatchPickerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ColorSwatchPickerPage;
